import React from 'react';
import { Grid } from 'antd';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
  CartesianGrid
} from 'recharts';
import { formatDate } from '../../utils/dateFormatters';

const { useBreakpoint } = Grid;

/**
 * DistributionChart - Stacked bar chart showing inventory distribution over time (Responsive)
 * Muestra cuantos productos (SKUs) hay en cada nivel de inventario por dia.
 * Cada barra muestra el total de productos divididos por su nivel de inventario disponible.
 * @param {Object} props
 * @param {Array} props.data - Array of DailyInventoryDistributionRecord
 * @param {boolean} props.groupByNit - Whether data is grouped by NIT
 */
function DistributionChart({ data, groupByNit = false }) {
  const screens = useBreakpoint();
  const isXs = !screens.xs;
  const isSm = screens.sm;
  const isMd = screens.md;

  // Transform data for chart
  const chartData = React.useMemo(() => {
    if (!data || data.length === 0) return [];

    if (groupByNit) {
      // Group by date and sum counts
      const groupedByDate = {};
      data.forEach(record => {
        const date = record.RecordDate;
        if (!groupedByDate[date]) {
          groupedByDate[date] = {
            RecordDate: date,
            NegroCount: 0,
            RojoCount: 0,
            AmarilloCount: 0,
            VerdeCount: 0,
            AzulCount: 0
          };
        }
        groupedByDate[date].NegroCount += record.NegroCount || 0;
        groupedByDate[date].RojoCount += record.RojoCount || 0;
        groupedByDate[date].AmarilloCount += record.AmarilloCount || 0;
        groupedByDate[date].VerdeCount += record.VerdeCount || 0;
        groupedByDate[date].AzulCount += record.AzulCount || 0;
      });
      return Object.values(groupedByDate).sort((a, b) =>
        a.RecordDate.localeCompare(b.RecordDate)
      );
    }

    return data.sort((a, b) => a.RecordDate.localeCompare(b.RecordDate));
  }, [data, groupByNit]);

  // Responsive chart height
  const chartHeight = isXs ? 250 : isSm ? 300 : 350;
  const marginValue = isXs ? 10 : 20;

  // Responsive font sizes
  const tickStyle = { fontSize: isXs ? 10 : 11 };
  const legendStyle = { fontSize: isXs ? 10 : 12 };

  // Action recommendations by color
  const ACTION_HINTS = {
    NegroCount: 'Pedido urgente',
    RojoCount: 'Pedir pronto',
    AmarilloCount: 'Monitorear',
    VerdeCount: 'Stock adecuado',
    AzulCount: 'Posible exceso'
  };

  // Custom tooltip
  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null;

    return (
      <div style={{
        backgroundColor: 'white',
        border: '1px solid #ccc',
        borderRadius: '4px',
        padding: '8px',
        fontSize: '12px'
      }}>
        <p style={{ margin: 0, fontWeight: 'bold', marginBottom: '4px' }}>{formatDate(label)}</p>
        {payload.map((entry, index) => (
          <p key={index} style={{ margin: '2px 0', color: entry.color }}>
            {entry.name}: {entry.value} productos <span style={{ fontSize: '10px', opacity: 0.7 }}>({ACTION_HINTS[entry.dataKey] || ''})</span>
          </p>
        ))}
      </div>
    );
  };

  if (!chartData || chartData.length === 0) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: chartHeight,
        color: '#999',
        fontSize: isXs ? 13 : 14
      }}>
        No hay datos disponibles
      </div>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={chartHeight}>
      <BarChart
        data={chartData}
        margin={{ top: marginValue, right: 30, left: 20, bottom: 5 }}
      >
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis
          dataKey="RecordDate"
          tickFormatter={formatDate}
          angle={isXs ? -90 : -45}
          textAnchor="end"
          height={isXs ? 60 : 80}
          style={tickStyle}
          interval={isXs ? 1 : 0}
        />
        <YAxis width={isXs ? 35 : 40} style={tickStyle} />
        <Tooltip content={<CustomTooltip />} />
        <Legend
          wrapperStyle={legendStyle}
          iconType={isXs ? 'circle' : 'rect'}
        />
        <Bar dataKey="NegroCount" stackId="a" name={isXs ? 'Negro' : 'Sin stock (Negro <10%)'} fill="#1f2937" />
        <Bar dataKey="RojoCount" stackId="a" name={isXs ? 'Rojo' : 'Muy bajo (Rojo 10-30%)'} fill="#dc2626" />
        <Bar dataKey="AmarilloCount" stackId="a" name={isXs ? 'Amarillo' : 'Moderado (Amarillo 30-70%)'} fill="#ca8a04" />
        <Bar dataKey="VerdeCount" stackId="a" name={isXs ? 'Verde' : 'Adecuado (Verde 70-100%)'} fill="#16a34a" />
        <Bar dataKey="AzulCount" stackId="a" name={isXs ? 'Azul' : 'Exceso (Azul >100%)'} fill="#2563eb" />
      </BarChart>
    </ResponsiveContainer>
  );
}

export default DistributionChart;
